/**
 * Per-org scoped Typesense search (#2404 / #2301 / #2300 / #2299).
 *
 * The Cmd+K global search (`typesense-service.ts`) ships a static, all-collections
 * search key and enforces org isolation only with a client-supplied `filter_by`
 * string — which the key does not enforce. These newer search paths instead mint
 * a per-org *scoped* key server-side (`search_getScopedSearchKey`) that has the
 * caller's organizationId embedded as a cryptographically signed filter. Typesense
 * ANDs that embedded filter into every query, so a holder of the key can only ever
 * read its own org's documents no matter what filter it sends.
 *
 * Architecture: Typesense is used as an INDEX (it answers "which document IDs
 * match this query"), and Firestore remains the source of truth (the caller
 * fetches the authoritative documents by ID). This fixes the old
 * `orderBy(...).limit(N)` window scans that silently truncated substring/contains
 * search once a collection grew past N rows.
 */

import { httpsCallable } from "../firebase/authReadyCallable";
import { functions } from "../../config/firebase";
import { isTypesenseEnabled } from "../../config/typesense";
import {
  searchCollection,
  toSearchGroup,
} from "../typesense-service";
import type { MultiSearchResult } from "../../types/search";

interface ScopedKeyResponse {
  key: string;
  /** Unix epoch seconds when the key expires. */
  expiresAt: number;
}

// Module-level cache of the scoped key. The key embeds the caller's org and an
// expiry; we refresh a bit before it lapses so an in-flight search never uses an
// expired key. A pending promise is cached too so concurrent callers share one mint.
let cachedKey: { key: string; expiresAt: number } | null = null;
let pendingKey: Promise<string> | null = null;

/**
 * Bumped by every {@link resetScopedSearchKeyCache} call, and captured by each
 * mint before it starts.
 *
 * A reset can null `cachedKey` and `pendingKey`, but it CANNOT cancel a callable
 * that is already in flight — that promise's `.then` still runs and would write
 * its result into the cache afterwards. On a user switch inside one SPA session
 * that means the PREVIOUS user's mint can resolve after the new user's and leave
 * their org's key cached, which is precisely the cross-org carryover the reset
 * exists to prevent. Comparing the captured generation before writing makes a
 * superseded mint unable to publish its result.
 *
 * This was a narrow window while keys were minted lazily on first search.
 * Prewarming at login ({@link prewarmSearchKeys}) means a mint is now reliably in
 * flight across exactly the transition that resets the cache, so the guard is
 * load-bearing rather than theoretical.
 */
let keyCacheGeneration = 0;

/** Refresh when within this many seconds of expiry. */
const KEY_REFRESH_SKEW_SECONDS = 5 * 60;

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * Refresh-ahead: a key minted at sign-in lapses about an hour later, and the re-mint then landed
 * on the user's next keystroke, cold start included (measured 2026-10-01: 1 of 352 mints took
 * 17.9 s). After each successful mint the next one is scheduled for when the cached key turns
 * stale, so a search never waits for it. A hidden tab waits until it is shown again rather than
 * minting all day in the background; a reset (auth-state change) cancels whatever is scheduled.
 */
export const MIN_REFRESH_DELAY_MS = 60_000;
const refreshTimers = new Map<string, ReturnType<typeof setTimeout>>();
const refreshOnShow = new Map<string, () => void>();

function cancelRefresh(name: string): void {
  const timer = refreshTimers.get(name);
  if (timer !== undefined) clearTimeout(timer);
  refreshTimers.delete(name);
  const onShow = refreshOnShow.get(name);
  if (onShow && typeof document !== "undefined") document.removeEventListener("visibilitychange", onShow);
  refreshOnShow.delete(name);
}

function scheduleRefresh(name: string, expiresAt: number, generation: number, mint: () => Promise<string>): void {
  cancelRefresh(name);
  const run = () => {
    if (generation !== keyCacheGeneration) return;
    void mint().catch(() => undefined);
  };
  // Floor: a key that is already stale (or a short-lived one) must not turn into a mint loop.
  const delayMs = Math.max(MIN_REFRESH_DELAY_MS, (expiresAt - KEY_REFRESH_SKEW_SECONDS - nowSeconds()) * 1000);
  refreshTimers.set(
    name,
    setTimeout(() => {
      refreshTimers.delete(name);
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        run();
        return;
      }
      const onShow = () => {
        if (document.visibilityState === "hidden") return;
        cancelRefresh(name);
        run();
      };
      refreshOnShow.set(name, onShow);
      document.addEventListener("visibilitychange", onShow);
    }, delayMs),
  );
}

/**
 * Clear the cached scoped key. MUST be called on any auth-state change
 * (login, logout, user switch) — the key embeds the previous user's org, so a
 * different user authenticating in the same SPA session (no full reload) would
 * otherwise keep searching under the prior org's scope until the ~55-min TTL
 * lapses. Wired into AuthContext's onAuthStateChange handler.
 */
export function resetScopedSearchKeyCache(): void {
  // Invalidates any mint already in flight so it cannot write a superseded
  // (previous user's) key into the cache after this returns.
  keyCacheGeneration += 1;
  cancelRefresh("scoped");
  cachedKey = null;
  pendingKey = null;
}

/** @deprecated test alias for {@link resetScopedSearchKeyCache}. */
export const __resetScopedKeyCacheForTests = resetScopedSearchKeyCache;

/**
 * Fetch (and cache) a per-org scoped Typesense search key from the backend.
 * The organizationId is resolved server-side from the authenticated session —
 * it is never passed from the client.
 */
export async function getScopedSearchKey(): Promise<string> {
  if (cachedKey && cachedKey.expiresAt - KEY_REFRESH_SKEW_SECONDS > nowSeconds()) {
    return cachedKey.key;
  }
  if (pendingKey) return pendingKey;

  const callable = httpsCallable<Record<string, never>, ScopedKeyResponse>(
    functions,
    "search_getScopedSearchKey",
  );

  const generation = keyCacheGeneration;
  pendingKey = callable({})
    .then((res) => {
      const { key, expiresAt } = res.data;
      if (!key) throw new Error("Scoped search key response was empty");
      // Only publish if no auth-state change superseded this mint while it was in
      // flight. The caller still gets the key it asked for (it is that session's
      // own org key) — it just must not become the cached key for whoever is
      // signed in now.
      if (generation === keyCacheGeneration) {
        cachedKey = { key, expiresAt };
        scheduleRefresh("scoped", expiresAt, generation, getScopedSearchKey);
      }
      return key;
    })
    .finally(() => {
      // Guarded for the same reason: an unconditional null here would clear a
      // NEWER mint's pending promise and start a duplicate mint.
      if (generation === keyCacheGeneration) {
        pendingKey = null;
      }
    });

  return pendingKey;
}

/**
 * Warm both scoped-key caches so the first search of a session does not pay for
 * minting one.
 *
 * Both keys are minted lazily from inside a React Query `queryFn`, so before this
 * existed the very first search a user ran blocked on a Cloud Function round trip
 * — `search_getScopedSearchKey` verifies the session, reads the caller's profile
 * from Firestore and calls the Typesense keys API — *and* on that function's cold
 * start, which on a low-traffic callable is seconds, not milliseconds. Every
 * subsequent search reused the module-level cache and felt fine, which is exactly
 * why the cost is easy to miss: it lands entirely on first impressions.
 *
 * Call this once the user is authenticated (AuthContext, right after the profile
 * loads). Fire-and-forget by design: a mint failure here is silent, because both
 * search paths already fall back correctly when no key is available, and this is
 * a latency optimization rather than a correctness step. No-ops when Typesense is
 * not configured, and cheap when the keys are already cached — the getters return
 * the cached value (or join the in-flight mint) without issuing a call.
 */
export function prewarmSearchKeys(): void {
  if (!isTypesenseEnabled()) return;
  void getScopedSearchKey().catch(() => undefined);
}

/**
 * Backtick-quote a value for safe inclusion in a Typesense `filter_by` string.
 * Values may contain `@`, `.`, spaces, etc. (emails). Any backtick in the value
 * is stripped so it can't break out of the quoting.
 */
function quoteFilterValue(value: string): string {
  return `\`${value.replace(/`/g, "")}\``;
}

/**
 * Build a Typesense exact multi-value filter: `field:[\`a\`,\`b\`]`.
 * Returns null when there are no values (caller should skip the search).
 */
export function buildExactInFilter(field: string, values: readonly string[]): string | null {
  const cleaned = Array.from(new Set(values.map((v) => v.trim()).filter(Boolean)));
  if (cleaned.length === 0) return null;
  return `${field}:[${cleaned.map(quoteFilterValue).join(",")}]`;
}

/**
 * The Typesense API key a single-collection search authenticates with: always the
 * per-org scoped key (org isolation signed into the key). There is no broad-key
 * fallback in the browser any more (#4031). `assets` is NOT on this key since #6079
 * (vehicle assignee/VIN/plate are Fleet P7 data): Cmd+K reaches it through
 * search_searchRestricted. Kept as the one routing point so a future per-module key
 * (#4031 step 2) changes one place.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function getSearchApiKey(_collection: string): Promise<string> {
  return getScopedSearchKey();
}

export interface ScopedSearchOptions {
  /** Query string. Use "*" to match all (filter-only searches). */
  q: string;
  /** Comma-separated query_by fields. Defaults to the collection's default set. */
  queryBy?: string;
  /** Additional filter_by (AND-combined with the key's embedded org filter). */
  filterBy?: string;
  /** Max hits to return. */
  perPage?: number;
  sortBy?: string;
}

/**
 * Run an org-scoped Typesense search and return the matching Firestore document
 * IDs, in Typesense result order. Returns [] when Typesense is not configured.
 * Throws if the search itself fails (callers surface this rather than silently
 * falling back to a truncating Firestore scan).
 */
export async function searchScopedIds(
  collection: string,
  options: ScopedSearchOptions,
): Promise<string[]> {
  if (!isTypesenseEnabled()) return [];

  const apiKey = await getScopedSearchKey();
  const result = await searchCollection<{ id?: string }>(collection, options.q, {
    apiKey,
    queryBy: options.queryBy,
    filterBy: options.filterBy,
    perPage: options.perPage,
    sortBy: options.sortBy,
  });

  return result.hits
    .map((h) => h.document?.id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}

interface RoleGatedSearchResponse {
  results: Array<{ collection: string; hits: unknown[]; found: number; error?: string }>;
  allowedCollections: string[];
}

/**
 * Search the ROLE-GATED collections (assets, investors, employees, issue_reports, contacts, quotes,
 * price_book_items and the QuickBooks ones: see ROLE_GATED_COLLECTIONS) through the server, which checks the caller's role on every query and filters to
 * their own org. The browser's search key cannot reach these collections at all.
 * Collections the caller may not read simply come back absent.
 * @param {string} query the search text
 * @param {string[]} collections role-gated collections to search
 * @param {number} perCollection hits per collection
 * @return {Promise<MultiSearchResult["results"]>} one group per searched collection
 */
export async function searchRoleGated(
  query: string,
  collections: string[],
  perCollection: number,
): Promise<MultiSearchResult["results"]> {
  const callable = httpsCallable<
    { query: string; collections: string[]; perCollection: number },
    RoleGatedSearchResponse
  >(functions, "search_searchRestricted");
  const res = await callable({ query, collections, perCollection });
  return (res.data.results ?? [])
    .filter((r) => !r.error)
    .map((r) => toSearchGroup(r.collection, r.hits ?? [], r.found ?? 0));
}

/**
 * One role-gated collection through the server, for the single-collection callers (PageSearch,
 * the customer picker). Unlike {@link searchRoleGated} this THROWS when the server's search of
 * the collection failed, so a failure never reads as "no results". Returns null when the server
 * did not search it for this caller (not permitted).
 * @param {string} collection the role-gated collection
 * @param {string} query the search text
 * @param {number} perPage hits to return (server caps it)
 * @return {Promise<MultiSearchResult["results"][number] | null>} the group, or null
 */
export async function searchRoleGatedOne(
  collection: string,
  query: string,
  perPage: number,
): Promise<MultiSearchResult["results"][number] | null> {
  const callable = httpsCallable<
    { query: string; collections: string[]; perCollection: number },
    RoleGatedSearchResponse
  >(functions, "search_searchRestricted");
  const res = await callable({ query, collections: [collection], perCollection: Math.min(Math.max(perPage, 1), 50) });
  const group = (res.data.results ?? []).find((r) => r.collection === collection);
  if (!group) return null;
  if (group.error) throw new Error(`Search failed for ${collection}`);
  return toSearchGroup(group.collection, group.hits ?? [], group.found ?? 0);
}
